import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { baseUrl } from '../../comman/config';
import { itemSelectOption, matchItemOption } from '../ItemName';

// Two opt-in props, both defaulting to the old behaviour so Menu Report -- the only other
// caller -- is untouched:
//   showExportPdf: the Consumption Report has no PDF export, so it opts out.
//   multiEvent:    the Consumption Report selects several events at once. Menu Report reads the
//                  event as a single object (event.value / event.label) and sends one event_id
//                  to its API, so it stays single-select.
//   handleClearFilters: Clear also resets the screen's inline per-column filters.
const MenuViewFilter = ({ handleFilterChange, handleExport, handleExportPdf, handleSubmitFilters,
    initialFromDate, initialToDate, initialItemValues, initialSessionValues,
    showExportPdf = true, multiEvent = false, handleClearFilters }) => {
    const today = new Date().toISOString().split('T')[0];
    const [fromDate, setFromDate] = useState(initialFromDate || today);
    const [toDate, setToDate] = useState(initialToDate || today);
    const [session, setSession] = useState([]);
    const [item, setItem] = useState([]);
    const [sessionList, setSessionList] = useState([]);
    const [itemList, setItemList] = useState([]);
    const [filter, setFilter] = useState(false);
    const [eventList, setEventList] = useState([]);
    // Array when multiEvent, single object otherwise -- react-select needs the shape to match isMulti.
    const ALL_EVENT = { value: 'all', label: 'All' };
    const [event, setEvent] = useState(multiEvent ? [ALL_EVENT] : {});

    useEffect(() => {
        getSessionList();
        getItemList();
        getEventList();
    }, []);

    // Seed the date span from the caller (e.g. the Consumption Report opened via
    // "Check RAS / Consumption"), so the applied event span shows as selected by default.
    useEffect(() => {
        if (initialFromDate) setFromDate(initialFromDate);
        if (initialToDate) setToDate(initialToDate);
        if (initialFromDate || initialToDate) setFilter(true);
    }, [initialFromDate, initialToDate]);

    // Seed the selected items once the item list has loaded. initialItemValues is a
    // comma-joined string of item ids (stable across renders); we resolve them to
    // labelled options so the Item dropdown reflects what the report is showing.
    useEffect(() => {
        if (!initialItemValues || !itemList.length) return;
        const ids = initialItemValues.split(',').filter(Boolean);
        if (!ids.length) return;
        const selected = itemList
            .filter((it) => ids.includes(String(it.id)))
            .map(itemSelectOption);
        setItem(selected);
        setFilter(true);
    }, [itemList, initialItemValues]);

    // Seed the selected session(s) from the caller, overriding the "All" default set in
    // getSessionList -- e.g. Check RAS opens the report for the one session that was planned.
    useEffect(() => {
        if (!initialSessionValues || !sessionList.length) return;
        const ids = initialSessionValues.split(',').filter(Boolean);
        if (!ids.length) return;
        const selected = sessionList
            .filter((s) => ids.includes(String(s.id)))
            .map((s) => ({ value: s.id, label: s.name }));
        if (selected.length) setSession(selected);
    }, [sessionList, initialSessionValues]);

    const getSessionList = async () => {
        const response = await axios.get(`${baseUrl}/comman/list?table=session_master&condition=status=1`);
        const sessionList = response.data.data;
        setSessionList(sessionList);
        // Default to every session so the multi-select shows the "All" chip (the display
        // logic below renders "All" when session.length === sessionList.length).
        setSession(sessionList.map((s) => ({ value: s.id, label: s.name })));
    }

    const getItemList = async () => {
        const response = await axios.get(`${baseUrl}/comman/list?table=item_master&condition=status=1`);
        const itemList = response.data.data;
        setItemList(itemList);
    }

    const getEventList = async () => {
        const response = await axios.get(`${baseUrl}/comman/list?table=event_master&condition=status=1`);
        const data = response.data.data;
        const newObject = { id: 'all', event_name: 'All' };
        data.unshift(newObject);
        setEventList(data);
        setEvent(multiEvent ? [ALL_EVENT] : ALL_EVENT);
    }

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        if (name === "from_date") {
            setFromDate(value);
            // Automatically apply filter when date changes
            handleFilterChange(value, toDate, session, item, event, false);
            setFilter(true);
        } else if (name === "to_date") {
            setToDate(value);
            // Automatically apply filter when date changes
            handleFilterChange(fromDate, value, session, item, event, false);
            setFilter(true);
        }
    };

    const handleSelectChange = (data, option) => {
        if (option.name === 'session') {
            // Handle "All" session logic
            if (data.some(d => d.value === 'all')) {
                setSession(sessionList.map(s => ({ value: s.id, label: s.name })));
            } else {
            setSession(data);
            }
            // Automatically apply filter when session changes
            handleFilterChange(fromDate, toDate, data, item, event);
        }
    };

    const defaultValues = {
        sessions: sessionList
            .filter((item) => item.id === 1 || item.id === 2)
            .map((item) => ({
                value: item.id,
                label: item.name,
            }))
    };

    const handleClear = () => {
        // Clear takes the inline per-column filters with it. Leaving them set would look like
        // Clear had emptied the report, when a column filter is still hiding the rows.
        handleClearFilters?.();
        // session/item must stay arrays (isMulti Selects + parent uses session.map).
        setSession([]);
        setItem([]);
        setFromDate(today);
        setToDate(today);
        setEvent(multiEvent ? [ALL_EVENT] : ALL_EVENT);
        setFilter(false);

        handleFilterChange("", "", [], [], multiEvent ? [ALL_EVENT] : ALL_EVENT, true);
    }

    const handleChangeMultiSelect = (selected, name) => {
        if (name === 'item') {
            setItem(selected);
            // Automatically apply filter when item selection changes
            handleFilterChange(fromDate, toDate, session, selected, event, false);
            setFilter(true);
        }
    };


    const handelEventChange = (selectedEvent) => {
        const data = eventList.filter(i => i.id == selectedEvent.value);
        setEvent({value: data[0].id, label: data[0].event_name});
        
        // If "All" is selected, don't set any date filters
        if (data[0].id === 'all') {
            // Pass empty strings for dates to show all data
            handleFilterChange("2024-01-01", today, session, item, {value: 'all', label: 'All'}, true);
        } else {
            // For specific events, use that event's dates
            setFromDate(data[0].start_date);
            setToDate(data[0].end_date);
            handleFilterChange(
                data[0].start_date, 
                data[0].end_date, 
                session, 
                item, 
                {value: data[0].id, label: data[0].event_name}, 
                true
            );
        }
        
        setFilter(true);
    };

    // Multi-select events: the filter carries ONE From/To pair, so several events collapse to the
    // earliest start and the latest end. Anything scheduled in the gaps between the selected
    // events therefore falls inside the span too -- widening the window, never narrowing it.
    // Selecting events only updates the filter -- the report is regenerated on Submit, like every
    // other control here. Ticking events one at a time would otherwise refetch on each click, and
    // a partial selection ("Paryushan 2024" before you add 2025) is rarely what you want reported.
    const handelEventMultiChange = (selected) => {
        const list = Array.isArray(selected) ? selected : [];
        const real = list.filter((o) => o.value !== 'all');
        // "All" starts selected, so it is still in the list when you tick your first real event.
        // Only treat it as a choice when it was just added -- otherwise it is leftover and gets
        // dropped, or the event you picked would be discarded and the box would snap back to All.
        const allJustClicked = list.some((o) => o.value === 'all') && !(event ?? []).some((o) => o.value === 'all');
        // "All", or clearing the box, drops the event narrowing. Dates are left as they are
        // rather than snapped back to a default, so the window doesn't jump under the user.
        if (allJustClicked || !real.length) {
            setEvent([ALL_EVENT]);
            handleFilterChange(fromDate, toDate, session, item, [ALL_EVENT], false);
            setFilter(true);
            return;
        }
        const picked = eventList.filter((e) => e.id !== 'all' && real.some((o) => o.value == e.id));
        const starts = picked.map((e) => e.start_date).filter(Boolean).sort();
        const ends = picked.map((e) => e.end_date).filter(Boolean).sort();
        const from = starts[0] ?? fromDate;
        const to = ends[ends.length - 1] ?? toDate;
        setEvent(real); // `real`, not `list` -- a leftover "All" chip alongside real events reads as a contradiction
        setFromDate(from);
        setToDate(to);
        handleFilterChange(from, to, session, item, real, false);
        setFilter(true);
    };

    const clickSubmit = () => {
        // Call the submit function passed from parent
        handleSubmitFilters();
    }

    const clickExport = () => {
        handleExport();
    }
    const clickExportPDF = () => {
        handleExportPdf();
    }

    return (
        <div className="flex flex-wrap items-center pb-4 gap-4">
            <div className="m-2">
                <Select
                    isMulti={multiEvent}
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    className={multiEvent ? 'basic-multi-select w-64' : 'basic-single w-48'}
                    value={event}
                    name="event_name"
                    onChange={(selectedOptions) => (multiEvent ? handelEventMultiChange(selectedOptions) : handelEventChange(selectedOptions))}
                    options={eventList.map(item => ({
                        value: item.id,
                        label: item.event_name
                    }))}
                    placeholder="Event List"
                    filterOption={(option, inputValue) => {
                        return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                />
            </div>
            <div className="m-2">
                <input
                    type="date"
                    className="block p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                    name="from_date"
                    onChange={handleDateChange}
                    value={fromDate}
                />
            </div>
            <div className="m-2">
                <input
                    type="date"
                    className="block p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500"
                    name="to_date"
                    onChange={handleDateChange}
                    value={toDate}
                />
            </div>
            <div className="m-2">
                <Select
                    isMulti
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    className="w-48"
                    value={session.length === sessionList.length ? [{ value: 'all', label: 'All' }] : session}
                    name="session"
                    onChange={(selectedOption) => {
                        if (selectedOption.some(option => option.value === 'all')) {
                            setSession(sessionList.map(item => ({
                                value: item.id,
                                label: item.name
                            })));
                            handleFilterChange(fromDate, toDate, sessionList.map(item => ({
                                value: item.id,
                                label: item.name
                            })), item, event, false);
                        } else {
                            setSession(selectedOption);
                            handleFilterChange(fromDate, toDate, selectedOption, item, event, false);
                        }
                    }}
                    options={[
                        { value: 'all', label: 'All' },
                        ...sessionList.map(item => ({
                            value: item.id,
                            label: item.name
                        }))
                    ]}
                    placeholder="Select Sessions"
                    filterOption={(option, inputValue) => {
                        return option.data.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                />
            </div>
            <div className="m-2">
                <Select
                    isMulti
                    classNamePrefix="select"
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    className="basic-multi-select w-72"
                    value={item}
                    name="item"
                    onChange={(selectedOptions) => handleChangeMultiSelect(selectedOptions, 'item')}
                    // The list reads in Gujarati and is searched in either language -- see
                    // components/ItemName.
                    options={itemList.map(itemSelectOption)}
                    placeholder="Item List"
                    filterOption={matchItemOption}
                />
            </div>
            <div className="m-2">
                {filter &&(
                    <>
                        <button
                            onClick={clickSubmit}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md mr-2 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                        >
                            Submit
                        </button>
                        <button
                            onClick={handleClear}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md mr-2 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                        >
                            Clear
                        </button>
                    </>

                )}
                <button
                    onClick={clickExport}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring focus:ring-gray-200"
                >
                    Export Excel
                </button>
                {showExportPdf && (
                    <button
                        onClick={clickExportPDF}
                        className="ml-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring focus:ring-gray-200"
                    >
                        Export PDF
                    </button>
                )}
            </div>
        </div>
    );
};

export { MenuViewFilter };